<template>
  <NuxtLink
    v-bind="{ to }"
    class="explore-more-teaser block w-full"
    :class="`explore-more-teaser-${variantConfig.cssClass}`"
  >
    <LcCard class="explore-more-teaser__card h-full" radius="large" shadow="none">
      <div class="explore-more-teaser__background" aria-hidden="true" />
      <div
        v-if="variantConfig.titleKey"
        class="explore-more-teaser__title-wrapper"
        aria-hidden="true"
      >
        <span class="explore-more-teaser__title text-uppercase">
          {{ $t(variantConfig.titleKey) }}
        </span>
      </div>

      <div class="explore-more-teaser__fade" aria-hidden="true" />
      <div class="explore-more-teaser__content flex items-center gap-050 p-200">
        <LcIcon :name="variantConfig.icon" size="medium" />
        <span class="flex-1 headline-h5-headline">
          {{ $t(variantConfig.labelKey) }}
        </span>
        <LcIcon class="ml-auto" name="chevron-right" size="medium" />
      </div>
    </LcCard>
  </NuxtLink>
</template>

<script lang="ts" setup>
import type { LcIconName } from '@lottocom/frontend-components'
import type { RouteLocationRaw } from 'vue-router'

export type ExploreMoreTeaserVariant = 'draw' | 'for-you' | 'keno' | 'scratch'

export type ExploreMoreTeaserProps = {
  to: RouteLocationRaw
  variant: ExploreMoreTeaserVariant
}

type VariantConfig = {
  cssClass: string
  icon: LcIconName
  labelKey: string
  titleKey?: string
}

const VARIANT_CONFIG: Record<ExploreMoreTeaserVariant, VariantConfig> = {
  'draw': {
    cssClass: 'draw',
    icon: 'orders',
    labelKey: 'exploremoreteaser.draw.label',
    titleKey: 'exploremoreteaser.draw.title',
  },
  ['for-you']: {
    cssClass: 'for-you',
    icon: 'star',
    labelKey: 'exploremoreteaser.foryou.label',
  },
  'keno': {
    cssClass: 'keno',
    icon: 'keno',
    labelKey: 'exploremoreteaser.keno.label',
    titleKey: 'exploremoreteaser.keno.title',
  },
  'scratch': {
    cssClass: 'scratch',
    icon: 'scratch-now',
    labelKey: 'exploremoreteaser.scratch.label',
    titleKey: 'exploremoreteaser.scratch.title',
  },
}

const props = defineProps<ExploreMoreTeaserProps>()

const variantConfig = computed(() => VARIANT_CONFIG[props.variant])
</script>

<style lang="scss" scoped>
.explore-more-teaser {
  $component: &;

  position: relative;
  height: 10rem;
  border-radius: token('size/radius/l');
  text-decoration: none;

  @include focus-state {
    &::before {
      inset: 0;
      border-radius: token('size/radius/l');
      box-shadow: 0 0 0 token('size/border/200') token('color/border/accent/focused/focused');
    }
  }

  &__card {
    position: relative;
    overflow: hidden;
    border-radius: token('size/radius/l');
  }

  &__background {
    position: absolute;
    inset: 0;
  }

  &__title-wrapper {
    position: absolute;
    top: 2rem;
    right: 0;
    z-index: 1;
    width: 9.625rem;
    background: linear-gradient(180deg, token('color/blue/950') 0%, token('color/blue/900') 100%);
    background-clip: text;
    text-align: center;
    transform: rotate(-4deg);
  }

  &__title {
    color: transparent;
    font-weight: token('font/weight/black');
    font-size: 1.563rem;
    line-height: 1.313rem;
  }

  &__fade {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 50%;
  }

  &__content {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    color: token('color/text/primary/primary');
  }

  &-scratch {
    #{$component} {
      &__background {
        background:
          url('~/assets/img/explore-more/explore-more-scratch.svg') right center / auto 100%
            no-repeat,
          linear-gradient(193deg, token('color/red/500') 8.97%, #ffe3eb 96.52%);
      }

      &__fade {
        background: linear-gradient(180deg, transparent 4.38%, #ffe3eb 65.58%);
      }
    }
  }

  &-keno {
    #{$component} {
      &__background {
        background:
          url('~/assets/img/explore-more/explore-more-keno.svg') right center / auto 100% no-repeat,
          linear-gradient(206deg, token('color/yellow/400') 4.66%, #fff9da 81.36%);
      }

      &__fade {
        background: linear-gradient(180deg, transparent 4.38%, #fff2b2 65.58%);
      }
    }
  }

  &-draw {
    #{$component} {
      &__background {
        background:
          url('~/assets/img/explore-more/explore-more-draw.svg') right center / auto 100% no-repeat,
          linear-gradient(206deg, token('color/blue/400') -0.09%, #c9eeff 83%);
      }

      &__fade {
        background: linear-gradient(to bottom, transparent 4%, #b8e9ff 66%);
      }
    }
  }

  &-for-you {
    #{$component} {
      &__background {
        background:
          url('~/assets/img/explore-more/explore-more-for-you.svg') right center / auto 100%
            no-repeat,
          linear-gradient(206deg, token('color/green/600') -0.09%, token('color/green/200') 83.45%);
      }

      &__fade {
        background: linear-gradient(180deg, transparent 4.38%, token('color/green/500') 65.58%);
      }
    }
  }
}
</style>
